import React from 'react';
import { Grid, List } from 'semantic-ui-react';

import { SPRAY_EVENT_WIZARD_APPLICATION_STEP } from 'services/sprayEventWizard/constants';
import FieldValues from 'components/FieldValues';
import WizardEventSummarySection from 'components/WizardEventSummarySection';
import SprayEventTypeCropGate from 'components/SprayEventTypeCropGate';
import { SprayEventOperation } from 'services/sprayEvents/types';
import { isApplicationTypeNeedHalfCalculation } from 'services/sprayEvents/utils';
import SummaryApplicationApplicatorListItem from './components/SummaryApplicationApplicatorListItem';
import SummaryApplicationWaterRateListItem from './components/SummaryApplicationWaterRateListItem';
import SummaryApplicationWaterTotalListItem from './components/SummaryApplicationWaterTotalListItem';
import SummaryApplicationSprayMethodListItem from './components/SummaryApplicationSprayMethodListItem';
import SummaryApplicationWaterSourceListItem from './components/SummaryApplicationWaterSourceListItem';
import SummaryApplicationJustification from './components/SummaryApplicationJustification';
import SummaryApplicationTechnicalPersonName from './components/SummaryApplicationTechnicalPersonName';
import SummaryApplicationIsFinalSpray from './components/SummaryApplicationIsFinalSpray';

interface Props {
    readOnly: boolean;
    sprayEventOperation: SprayEventOperation;
}

export default function SummaryApplication({
    readOnly,
    sprayEventOperation
}: Props): React.ReactElement {
    return (
        <WizardEventSummarySection
            title="Application"
            sprayEventWizardStep={SPRAY_EVENT_WIZARD_APPLICATION_STEP}
            readOnly={readOnly}
        >
            <List>
                <Grid>
                    <FieldValues
                        fieldNames={[
                            'sprayMethod',
                            'sprayApplicationRate',
                            'sprayWaterSourceId',
                            'sprayEntityIds',
                            'justification',
                            'technicalPersonName',
                            'isFinalSpray',
                            'applicationType'
                        ]}
                    >
                        {({
                            sprayMethod,
                            sprayApplicationRate,
                            sprayWaterSourceId,
                            sprayEntityIds,
                            justification,
                            technicalPersonName,
                            isFinalSpray,
                            applicationType
                        }): React.ReactElement => {
                            const needHalfCalculation =
                                isApplicationTypeNeedHalfCalculation(
                                    applicationType
                                );

                            return (
                                <React.Fragment>
                                    <Grid.Row>
                                        <Grid.Column
                                            mobile={8}
                                            computer={4}
                                            tablet={4}
                                        >
                                            <SummaryApplicationApplicatorListItem
                                                operatorPersonId={
                                                    sprayEventOperation?.operatorPersonId
                                                }
                                            />
                                        </Grid.Column>
                                        <Grid.Column
                                            mobile={8}
                                            computer={4}
                                            tablet={4}
                                        >
                                            <SummaryApplicationTechnicalPersonName
                                                technicalPersonName={
                                                    technicalPersonName
                                                }
                                            />
                                        </Grid.Column>
                                        <Grid.Column
                                            mobile={8}
                                            computer={4}
                                            tablet={4}
                                        >
                                            <SummaryApplicationWaterRateListItem
                                                applicationRate={
                                                    sprayApplicationRate
                                                }
                                            />
                                        </Grid.Column>
                                        <Grid.Column
                                            mobile={8}
                                            computer={4}
                                            tablet={4}
                                        >
                                            <SummaryApplicationWaterTotalListItem
                                                waterRate={sprayApplicationRate}
                                                entityIds={sprayEntityIds}
                                                equipmentSetId={
                                                    sprayEventOperation?.sprayEquipmentSetId
                                                }
                                                applicationRate={
                                                    sprayApplicationRate
                                                }
                                                needHalfCalculation={
                                                    needHalfCalculation
                                                }
                                            />
                                        </Grid.Column>
                                    </Grid.Row>
                                    <Grid.Row>
                                        <Grid.Column
                                            mobile={8}
                                            computer={4}
                                            tablet={4}
                                        >
                                            <SummaryApplicationSprayMethodListItem
                                                sprayMethod={sprayMethod}
                                            />
                                        </Grid.Column>
                                        <Grid.Column
                                            mobile={8}
                                            computer={4}
                                            tablet={4}
                                        >
                                            <SummaryApplicationWaterSourceListItem
                                                waterSourceId={
                                                    sprayWaterSourceId
                                                }
                                            />
                                        </Grid.Column>
                                        <Grid.Column
                                            mobile={8}
                                            computer={4}
                                            tablet={4}
                                        >
                                            <SummaryApplicationJustification
                                                justification={justification}
                                            />
                                        </Grid.Column>
                                        <SprayEventTypeCropGate>
                                            <Grid.Column
                                                mobile={8}
                                                computer={4}
                                                tablet={4}
                                            >
                                                <SummaryApplicationIsFinalSpray
                                                    isFinalSpray={isFinalSpray}
                                                />
                                            </Grid.Column>
                                        </SprayEventTypeCropGate>
                                    </Grid.Row>
                                </React.Fragment>
                            );
                        }}
                    </FieldValues>
                </Grid>
            </List>
        </WizardEventSummarySection>
    );
}
